import { FadeIn } from "@/components/FadeIn";
import { BookOpen, Scale, Mountain } from "lucide-react";

const MISSION_IMAGE =
  "https://customer-assets.emergentagent.com/job_pond-education-hub/artifacts/gi24ak4q_2aae4fde-18bb-4942-81dd-379200be37c6.png";

const PRINCIPLES = [
  {
    icon: BookOpen,
    title: "Education before investment",
    body: (
      <>
        Good decisions start with understanding. Learning
        <br /> about your property, water, and options helps create
        <br /> a stronger foundation for your project.
      </>
    ),
  },
  {
    icon: Scale,
    title: "Independence, absolutely",
    body: (
      <>
        Our focus is knowledge, not products or services.
        <br className="hidden lg:block" /> We provide independent guidance based on your property, goals, and local conditions.
      </>
    ),
  },
  {
    icon: Mountain,
    title: "Alberta, specifically",
    body: (
      <>
        Prairie winters, water rights, short seasons,
        <br className="hidden lg:block" /> provincial regulation — everything we teach
        <br className="hidden lg:block" /> is grounded in this province.
      </>
    ),
  },
];

export const WhyAPN = () => {
  return (
    <section id="why" data-testid="mission-section" className="bg-background overflow-hidden">
      {/* Asymmetric spread: image left (bleeding off the edge), text right — heights matched */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-y-12 items-center pt-[50px] lg:pt-0">
        {/* Borderless image with the founding quote overlaid — touches the hero above on desktop */}
        <div className="lg:col-span-6 order-2 lg:order-1 lg:self-start">
          <FadeIn>
            <div className="relative img-grade h-[52vh] lg:h-[536px]">
              <img
                src={MISSION_IMAGE}
                alt="A farm pond reflecting the sky, with a white barn and farmhouse on green Alberta parkland"
                className="editorial-img"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/15 to-transparent" aria-hidden="true" />
              <blockquote
                data-testid="mission-pullquote"
                className="absolute inset-x-0 bottom-0 p-8 sm:p-12"
              >
                <p className="font-serif-heading italic text-xl sm:text-3xl leading-[1.2] text-white">
                  “We don't sell ponds.
                  <br /> We help you understand them.”
                </p>
              </blockquote>
            </div>
          </FadeIn>
        </div>

        <div className="lg:col-span-6 order-1 lg:order-2 lg:self-start lg:pt-20 pl-5 sm:pl-8 lg:pl-16 pr-5 sm:pr-8 lg:pr-[max(2rem,calc((100vw-1280px)/2+2rem))]">
          <FadeIn>
            <h2 className="font-serif-heading font-medium text-4xl sm:text-5xl lg:text-[clamp(2.25rem,2.9vw+0.5rem,3.4rem)] leading-[1.05] tracking-[-0.015em] text-foreground">
              Why we exist
            </h2>

            <div className="mt-6 lg:mt-10 space-y-6 text-base sm:text-lg leading-[1.9] text-foreground/80 max-w-[58ch] lg:max-w-none">
              <p className="lg:w-[580px] lg:max-w-none">
                Interest in pond aquaculture is growing across Alberta.
                Landowners, farmers, and rural property owners are exploring
                whether ponds can support recreation, food production, or new
                income opportunities. At the same time, finding clear,
                practical information can be difficult, especially for those
                just getting started.
              </p>
              <p>
                Pond & Aquaculture Resource Centre was created to provide education and
                guidance for people considering pond aquaculture. Through
                courses, workshops, and consulting, we help landowners better
                understand their property, local conditions, and the factors
                involved in planning and managing a pond. Our goal is to give
                people the knowledge they need to make informed decisions
                about their projects.
              </p>
            </div>
          </FadeIn>
        </div>
      </div>

      {/* Principles — quiet editorial columns with subtle dividers */}
      <div className="mx-auto w-full max-w-[1280px] px-5 sm:px-8 pt-[66px] lg:pt-20 pb-[50px] lg:pb-20">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-y-[41px] md:gap-y-0 md:divide-x md:divide-[color:var(--apn-border)]">
          {PRINCIPLES.map((p, i) => {
            const Icon = p.icon;
            return (
              <FadeIn key={p.title} delay={i * 0.08} className="h-full">
                <div
                  data-testid={`mission-principle-${i + 1}`}
                  className={`h-full ${i === 0 ? "md:pr-[28px]" : i === 1 ? "md:px-[28px]" : "md:pl-[28px]"}`}
                >
                  <Icon
                    className="h-6 w-6 text-[color:var(--apn-forest)]"
                    strokeWidth={1.25}
                    aria-hidden="true"
                  />
                  <h3 className="mt-6 font-serif-heading text-xl sm:text-2xl font-medium leading-snug text-foreground">
                    {p.title}
                  </h3>
                  <p className="mt-4 text-sm sm:text-[15px] leading-[1.8] text-foreground/60">
                    {p.body}
                  </p>
                </div>
              </FadeIn>
            );
          })}
        </div>
      </div>
    </section>
  );
};
